import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
  type ColumnDef,
  type ExpandedState,
} from '@tanstack/react-table';
import { Camera, Check as CheckIcon, ExternalLink, Search, X } from 'lucide-react';
import {
  DEALER_STOCK_OPTIONS,
  HOME_VALUES,
  NO_CURRENT_VALUE,
  QUEUE_REASON_CODES,
  REASON_LABELS,
  VENDOR_STOCK_OPTIONS,
  bulkReviewHomeValue,
  fetchHomeValueChecks,
  fetchHomeValueManufacturers,
  fetchHomeValueVendors,
  isSuperseded,
  reviewHomeValueCheck,
} from '../api/home_value_checks';
import type {
  DealerStockFilter,
  HomeValueCheck,
  HomeValueFilters,
  HomeValueReasonCode,
  HomeValueReviewAction,
  VendorOption,
  VendorStockFilter,
} from '../api/home_value_checks';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { FilterSelect } from '../components/ui/filter-select';
import { MultiSelect } from '../components/ui/multi-select';
import { DataTable } from '../components/data-table/DataTable';
import {
  ACTIONS_COL_WIDTH,
  ExpandedFields,
  ExpandedPanel,
  ExpandedSection,
  ScreenshotLink,
  expandColumn,
  selectColumn,
} from '../components/data-table/columns';
import {
  LocationChips,
  dealerColumn,
  locationsColumn,
  manufacturerColumn,
  nsIdColumn,
  nsStockColumn,
  observedColumn,
  reasonColumn,
  skuColumn,
  transitionColumn,
  vendorColumn,
  vendorTotalColumn,
} from '../components/data-table/homeValueColumns';
import {
  RowActionButton,
  RowActionMenu,
  RowActions,
  type RowMenuItem,
} from '../components/data-table/RowActions';
import {
  BulkActionBar,
  ColumnsMenu,
  FilterBar,
  FilterField,
  FilterRow,
  PageHeading,
  ResetFiltersButton,
  TablePagination,
} from '../components/data-table/TableChrome';
import { MAGENTO_BASE_URL, NS_BASE_URL } from '../config/external_links';
import { fmtDate, fmtDateTime, fmtVendor } from '../utils/format';
import { takeRows } from '../utils/optimistic';
import { useUrlFilters } from '../hooks/useUrlFilters';
import { describePartialReview, describeReviewFailure } from '../utils/reviewErrors';

const PAGE_SIZES = [50, 100, 250, 500, 1000] as const;

const HOME_VALUE_OPTIONS = [
  { value: '', label: 'All' },
  ...HOME_VALUES.map(v => ({ value: v, label: v })),
];

const CURRENT_VALUE_OPTIONS = [
  ...HOME_VALUE_OPTIONS,
  { value: NO_CURRENT_VALUE, label: 'No value set' },
];

/**
 * Home Value → Review.
 *
 * Every proposal here says: the vendor's stock says this item should promise X,
 * NetSuite currently promises Y. Approving PATCHes one NetSuite attribute; the
 * next ns_sync tick pulls the new value back and Celigo carries it to Magento.
 * An approved row moves to Queue until the push lands, then to Processed —
 * the same three tabs as Price Checks.
 *
 * Modelled on ChecksPage (bulk select + review): this IS the decision screen.
 */
export default function HomeValueReviewPage() {
  const { user } = useAuth();
  const canReview = user?.role === 'reviewer' || user?.role === 'admin';

  const [filters, setFilters] = useState<HomeValueFilters>({
    review_status: 'pending', page: 1, per_page: 50,
  });
  // Vendor lives in the URL so the dashboard's Pending by vendor bars can open
  // this page pre-filtered (?vendor=pac0); Reset clears it from the URL too.
  const [urlFilters, setUrlFilters] = useUrlFilters({ vendor: [] as string[] });
  const vendorCodes = urlFilters.vendor;
  const setVendorCodes = (next: string[]) => setUrlFilters({ vendor: next });
  const [reasonCodes, setReasonCodes]   = useState<string[]>([]);
  const [manufacturers, setManufacturers] = useState<string[]>([]);
  const [vendorOptions, setVendorOptions] = useState<VendorOption[]>([]);
  const [manufacturerOptions, setManufacturerOptions] = useState<string[]>([]);
  const [search, setSearch] = useState('');

  const [items, setItems]     = useState<HomeValueCheck[]>([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [busy, setBusy]       = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionNotice, setActionNotice] = useState<string | null>(null);

  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({
    // Available but off by default — the table already carries the stock chips,
    // which are the wide column that matters.
    manufacturer: false,
  });


  // The row-action closures live inside a `columns` useMemo keyed on
  // [canReview, busy], so they capture whatever `items` was when the memo last
  // ran — which is the empty array from the first render. Reading the list
  // through a ref keeps the optimistic snapshot honest; without it a failed
  // call "restores" an empty snapshot and the row is lost from the table.
  const itemsRef = useRef<HomeValueCheck[]>([]);
  useEffect(() => { itemsRef.current = items; }, [items]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setRowSelection({});
    setExpanded({});
    try {
      const data = await fetchHomeValueChecks({
        ...filters,
        vendor_codes: vendorCodes.length ? vendorCodes : undefined,
        reason_codes: reasonCodes.length ? (reasonCodes as HomeValueReasonCode[]) : undefined,
        // The endpoint takes a single manufacturer; the control is a multi-select
        // for consistency with the other filters, so send the most recent pick.
        manufacturer: manufacturers[0],
        q: search.trim() || undefined,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [filters, vendorCodes, reasonCodes, manufacturers, search]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    fetchHomeValueVendors().then(setVendorOptions).catch(() => setVendorOptions([]));
    fetchHomeValueManufacturers()
      .then(setManufacturerOptions)
      .catch(() => setManufacturerOptions([]));
  }, []);

  /** Optimistically remove rows and run the call. On a failure — or when a bulk
   *  call moved fewer rows than were sent — reload from the server instead of
   *  restoring the snapshot: a request the gateway timed out may still land on
   *  the server, so "restored" rows could already be decided (BOT-449). */
  async function runOptimistic(ids: number[], call: () => Promise<unknown>, label: string) {
    const { remaining, snapshot } = takeRows(itemsRef.current, ids);
    setActionError(null);
    setActionNotice(null);
    setBusy(true);
    setItems(remaining);
    setTotal(t => t - snapshot.length);
    setRowSelection({});
    try {
      const updated = await call();
      const partial = typeof updated === 'number'
        ? describePartialReview(label, ids.length, updated)
        : null;
      if (partial) {
        setActionNotice(partial);
        await load();
      }
    } catch (e) {
      setActionError(describeReviewFailure(label, e));
      await load();
    } finally {
      setBusy(false);
    }
  }


  async function handleReview(id: number, action: HomeValueReviewAction) {
    await runOptimistic(
      [id],
      () => reviewHomeValueCheck(id, action),
      action === 'approved' ? 'Approve' : 'Reject',
    );
  }

  const selectedIds = useMemo(
    () => Object.keys(rowSelection).filter(k => rowSelection[k]).map(Number),
    [rowSelection],
  );

  async function handleBulk(action: HomeValueReviewAction) {
    if (selectedIds.length === 0) return;
    await runOptimistic(
      selectedIds,
      () => bulkReviewHomeValue(selectedIds, action),
      action === 'approved' ? 'Bulk approve' : 'Bulk reject',
    );
  }

  function resetFilters() {
    setVendorCodes([]);
    setReasonCodes([]);
    setManufacturers([]);
    setSearch('');
    setFilters({ review_status: 'pending', page: 1, per_page: filters.per_page });
  }

  const hasActiveFilters =
    vendorCodes.length > 0 ||
    reasonCodes.length > 0 ||
    manufacturers.length > 0 ||
    !!search ||
    !!filters.proposed_home_value ||
    !!filters.current_home_value ||
    !!filters.vendor_stock || !!filters.dealer_stock ||
    !!filters.observed_from || !!filters.observed_to;

  const columns = useMemo<ColumnDef<HomeValueCheck>[]>(() => {
    const cols: ColumnDef<HomeValueCheck>[] = [];
    if (canReview) cols.push(selectColumn<HomeValueCheck>());
    cols.push(expandColumn<HomeValueCheck>());
    cols.push(
      skuColumn,
      vendorColumn,
      manufacturerColumn,
      nsIdColumn,
      transitionColumn,
      reasonColumn,
      locationsColumn,
      vendorTotalColumn,
      dealerColumn,
      observedColumn,
      nsStockColumn,
    );
    cols.push({
      id: 'actions',
      size: ACTIONS_COL_WIDTH,
      enableHiding: false,
      meta: { label: 'Actions', align: 'right', padding: 'tight' },
      header: 'Actions',
      cell: ({ row }) => {
        const item = row.original;
        const menuItems: RowMenuItem[] = [
          {
            label: 'Details',
            icon: Search,
            href: `/all-items/${item.ns_item_id}?from=home-value-review&checkId=${item.id}`,
          },
          {
            label: 'Open in NetSuite',
            icon: ExternalLink,
            href: `${NS_BASE_URL}${item.ns_item_id}`,
          },
        ];
        if (item.magento_id != null) {
          menuItems.push({
            label: 'Open in Magento',
            icon: ExternalLink,
            href: `${MAGENTO_BASE_URL}${item.magento_id}`,
          });
        }
        if (item.screenshot_url) {
          menuItems.push({
            label: 'View screenshot',
            icon: Camera,
            href: item.screenshot_url,
            separatorBefore: true,
          });
        }
        return (
          <RowActions>
            {canReview && (
              <RowActionButton
                label="Approve — write to NetSuite"
                icon={CheckIcon}
                tone="success"
                disabled={busy}
                onClick={() => void handleReview(item.id, 'approved')}
              />
            )}
            {canReview && (
              <RowActionButton
                label="Reject — keep the current value"
                icon={X}
                disabled={busy}
                onClick={() => void handleReview(item.id, 'rejected')}
              />
            )}
            <RowActionMenu items={menuItems} />
          </RowActions>
        );
      },
    });
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canReview, busy]);

  const table = useReactTable({
    data: items,
    columns,
    getRowId: row => String(row.id),
    state: { rowSelection, expanded, columnVisibility },
    onRowSelectionChange: setRowSelection,
    onExpandedChange: setExpanded,
    onColumnVisibilityChange: setColumnVisibility,
    getRowCanExpand: () => true,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    initialState: {
      columnPinning: { left: ['select', 'expand', 'sku'], right: ['actions'] },
    },
  });

  const perPage    = filters.per_page ?? 50;
  const page       = filters.page ?? 1;
  const totalPages = Math.max(Math.ceil(total / perPage), 1);

  return (
    <>
      <PageHeading title="Home Value — Review" count={total} />

      <FilterBar>
        <FilterRow>
          <MultiSelect
            label="Vendor"
            options={vendorOptions.map(v => ({ value: v.code, label: fmtVendor(v.name) }))}
            selected={vendorCodes}
            onChange={next => { setVendorCodes(next); setFilters(f => ({ ...f, page: 1 })); }}
          />
          <FilterSelect
            label="From"
            options={CURRENT_VALUE_OPTIONS}
            value={filters.current_home_value ?? ''}
            onChange={v =>
              setFilters(f => ({ ...f, current_home_value: v || undefined, page: 1 }))
            }
          />
          <FilterSelect
            label="To"
            options={HOME_VALUE_OPTIONS}
            value={filters.proposed_home_value ?? ''}
            onChange={v =>
              setFilters(f => ({ ...f, proposed_home_value: v || undefined, page: 1 }))
            }
          />
          <MultiSelect
            label="Reason"
            options={QUEUE_REASON_CODES.map(rc => ({ value: rc, label: REASON_LABELS[rc] }))}
            selected={reasonCodes}
            onChange={next => { setReasonCodes(next); setFilters(f => ({ ...f, page: 1 })); }}
            searchPlaceholder="Search reasons…"
          />
          <MultiSelect
            label="Manufacturer"
            options={manufacturerOptions.map(m => ({ value: m, label: m }))}
            selected={manufacturers}
            onChange={next => {
              setManufacturers(next.slice(-1));
              setFilters(f => ({ ...f, page: 1 }));
            }}
            searchPlaceholder="Search manufacturers…"
          />
          <FilterSelect
            label="Vendor stock"
            options={VENDOR_STOCK_OPTIONS}
            value={filters.vendor_stock ?? ''}
            onChange={v =>
              setFilters(f => ({ ...f, vendor_stock: (v || undefined) as VendorStockFilter | undefined, page: 1 }))
            }
          />
          <FilterSelect
            label="Dealer stock"
            options={DEALER_STOCK_OPTIONS}
            value={filters.dealer_stock ?? ''}
            onChange={v =>
              setFilters(f => ({ ...f, dealer_stock: (v || undefined) as DealerStockFilter | undefined, page: 1 }))
            }
          />
        </FilterRow>

        <FilterRow className="gap-4">
          <FilterField label="Search">
            <Input
              placeholder="SKU or name"
              className="w-[200px]"
              value={search}
              onChange={e => { setSearch(e.target.value); setFilters(f => ({ ...f, page: 1 })); }}
            />
          </FilterField>

          <FilterField label="Observed">
            <Input
              type="date"
              className="w-[140px]"
              value={filters.observed_from ?? ''}
              onChange={e =>
                setFilters(f => ({ ...f, observed_from: e.target.value || undefined, page: 1 }))
              }
            />
            <span className="text-zinc-300">–</span>
            <Input
              type="date"
              className="w-[140px]"
              value={filters.observed_to ?? ''}
              onChange={e =>
                setFilters(f => ({ ...f, observed_to: e.target.value || undefined, page: 1 }))
              }
            />
          </FilterField>

          <div className="ml-auto flex items-center gap-2">
            {hasActiveFilters && <ResetFiltersButton onClick={resetFilters} />}
            <ColumnsMenu table={table} />
          </div>
        </FilterRow>
      </FilterBar>

      {actionNotice && (
        <div className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-[13px] text-amber-800">
          <span>{actionNotice}</span>
          <Button variant="ghost" size="sm" onClick={() => setActionNotice(null)} className="text-amber-700">
            Dismiss
          </Button>
        </div>
      )}

      {actionError && (
        <div className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-[13px] text-red-700">
          <span>{actionError}</span>
          <Button variant="ghost" size="sm" onClick={() => setActionError(null)} className="text-red-600">
            Dismiss
          </Button>
        </div>
      )}

      {canReview && selectedIds.length > 0 && (
        <BulkActionBar count={selectedIds.length} total={total} onClear={() => setRowSelection({})}>
          <Button variant="outline" size="sm" disabled={busy} onClick={() => void handleBulk('approved')}>
            <CheckIcon className="h-3.5 w-3.5 text-green-600" strokeWidth={2.5} />
            Approve
          </Button>
          <Button variant="outline" size="sm" disabled={busy} onClick={() => void handleBulk('rejected')}>
            <X className="h-3.5 w-3.5 text-zinc-500" />
            Reject
          </Button>
        </BulkActionBar>
      )}

      <DataTable
        table={table}
        loading={loading}
        error={error}
        emptyMessage="No Home Value proposals to review"
        renderExpanded={row => {
          const item = row.original;
          const inputs = item.rule_inputs ?? {};
          return (
            <ExpandedPanel>
              <ExpandedSection title="Item">
                <ExpandedFields
                  rows={[
                    ['Description', item.item_name ?? '—'],
                    ['Manufacturer', item.manufacturer ?? '—'],
                    ['Vendor part no.', <span className="font-mono">{item.sku ?? '—'}</span>],
                    ['NetSuite Home Value', item.ns_home_value_now ?? '—'],
                    ['On hand / available', `${item.ns_on_hand ?? '—'} / ${item.ns_available ?? '—'}`],
                  ]}
                />
              </ExpandedSection>

              <ExpandedSection title="Vendor stock at decision time">
                <LocationChips locations={item.locations} />
                <ExpandedFields
                  rows={[
                    ['PDC total', item.pdc_total ?? '—'],
                    ['Dealer stock', item.dealer_total ?? '—'],
                    ['Observed', fmtDateTime(item.availability_observed_at)],
                    [
                      'Latest scrape',
                      isSuperseded(item)
                        ? `${fmtDate(item.live_observed_at)} — newer than this proposal`
                        : fmtDate(item.live_observed_at),
                    ],
                    ['Rule', String(inputs.rule ?? '—')],
                    [
                      'Matched',
                      Array.isArray(inputs.matched) && inputs.matched.length
                        ? (inputs.matched as string[]).join(', ')
                        : '—',
                    ],
                  ]}
                />
              </ExpandedSection>

              <ExpandedSection title="Vendor screenshot">
                <ScreenshotLink url={item.screenshot_url} />
              </ExpandedSection>
            </ExpandedPanel>
          );
        }}
      />

      <TablePagination
        page={page}
        totalPages={totalPages}
        total={total}
        perPage={perPage}
        pageSizes={PAGE_SIZES}
        selectedCount={selectedIds.length}
        onPageChange={p => setFilters(f => ({ ...f, page: p }))}
        onPerPageChange={n => setFilters(f => ({ ...f, per_page: n, page: 1 }))}
      />
    </>
  );
}
